import { useContext, useCallback } from "react";
import { ServiceContext, TestDetailsContext, UserContext } from "@app/provider";
import { APIContext } from "@app/pippa/provider";
import {
  PippaAnalyticsEvent,
  PippaIngressSource,
} from "@app/pippa/models/analytics";
import {
  detectDeviceType,
  detectOS,
  detectBrowser,
} from "@app/pippa/helpers/deviceDetection";

type HeadersType = {
  "Content-Type": string;
  Authorization?: string;
};

export const usePippaAnalytics = () => {
  const { sessionService } = useContext(ServiceContext);
  const { apiProviderState } = useContext(APIContext);
  const { uppUser } = useContext(UserContext);
  const { getTestDetails } = useContext(TestDetailsContext);

  const getHeaders = useCallback(async (): Promise<HeadersType> => {
    const headers: HeadersType = {
      "Content-Type": "application/json;charset=UTF-8",
    };
    const token = await sessionService.getToken();
    if (token?.accessToken) {
      headers.Authorization = `Bearer ${token.accessToken}`;
    }
    return headers;
  }, [sessionService]);

  const trackEvent = useCallback(
    async (
      eventName: PippaAnalyticsEvent,
      payload: Record<string, unknown> = {}
    ) => {
      const chatId = apiProviderState.chatId;
      const patientUid = uppUser?.patientUID;
      const limsCaseId = getTestDetails()?.limsId?.toString();

      try {
        const headers = await getHeaders();
        await fetch("/pippa/api/analytics/events", {
          method: "POST",
          headers,
          body: JSON.stringify({
            eventName,
            chatId,
            payload,
            patient_uid: patientUid,
            lims_case_id: limsCaseId,
            device_type: detectDeviceType(),
            operating_system: detectOS(),
            browser: detectBrowser(),
          }),
        });
      } catch {
        // Fire-and-forget — never block UI
      }
    },
    [apiProviderState.chatId, uppUser?.patientUID, getTestDetails, getHeaders]
  );

  const trackIngress = useCallback(
    (source: PippaIngressSource) => {
      trackEvent(PippaAnalyticsEvent.CHATBOT_INGRESS, {
        chatbot_ingress: source,
      });
    },
    [trackEvent]
  );

  const trackConsentYes = useCallback(() => {
    trackEvent(PippaAnalyticsEvent.CHATBOT_CONSENT_YES, {});
  }, [trackEvent]);

  const trackConsentClosed = useCallback(
    (source?: PippaIngressSource) => {
      trackEvent(PippaAnalyticsEvent.CHATBOT_CONSENT_CLOSED, {
        chatbot_ingress: source,
      });
    },
    [trackEvent]
  );

  const trackBannerShown = useCallback(() => {
    trackEvent(PippaAnalyticsEvent.CHATBOT_BANNER_SHOWN, {});
  }, [trackEvent]);

  const trackChatbotClosed = useCallback(() => {
    trackEvent(PippaAnalyticsEvent.CHATBOT_CLOSED, {});
  }, [trackEvent]);

  const trackUserPlatform = useCallback(() => {
    trackEvent(PippaAnalyticsEvent.USER_PLATFORM, {
      device_type: detectDeviceType(),
      operating_system_version: detectOS(),
      browser: detectBrowser(),
    });
  }, [trackEvent]);

  const trackLinkClick = useCallback(
    (hyperlink?: string, phoneNumber?: string) => {
      trackEvent(PippaAnalyticsEvent.CHATBOT_LINKS, {
        hyperlink,
        phone_number: phoneNumber,
      });
    },
    [trackEvent]
  );

  return {
    trackEvent,
    trackIngress,
    trackConsentYes,
    trackConsentClosed,
    trackBannerShown,
    trackChatbotClosed,
    trackUserPlatform,
    trackLinkClick,
  };
};
